import {useEffect,useState} from 'react';

/** FloatHelm Cashbox is a location-level workspace, not a cash-tender form.
 * Cash tender records an amount; physical denominations are counted here.
 */
type Register={openingCents:number;netSalesCents:number;expectedCents:number;
  saleCount:number;synthetic:boolean};
type Count={id:string;countedAt:string;countedCents:number;expectedCents:number;
  varianceCents:number;denominations:Record<string,number>;note:string};
type CashSale={transactionId:string;createdAt:string;customerName:string;
  totalCents:number;receivedCents:number;changeCents:number;lineCount:number};
const bills=[10000,5000,2000,1000,500,100,25,10,5,1] as const;
const money=(cents:number)=>new Intl.NumberFormat('en-US',{
  style:'currency',currency:'USD',
}).format(cents/100);
const name=(value:number)=>value>=100?'$'+value/100:value+'¢';
const countValue=(counts:Record<string,number>)=>Object.entries(counts)
  .reduce((sum,[unit,qty])=>sum+Number(unit)*qty,0);

export function CashboxWorkspace({locationCode}: {locationCode:string}){
  const [register,setRegister]=useState<Register|null>(null);
  const [counts,setCounts]=useState<Count[]>([]);
  const [sales,setSales]=useState<CashSale[]>([]);
  const [billsCount,setBillsCount]=useState<Record<string,number>>({});
  const [note,setNote]=useState('');
  const [busy,setBusy]=useState(false);
  const [saving,setSaving]=useState(false);
  const [error,setError]=useState('');
  const [result,setResult]=useState('');
  const reload=async()=>{
    const suffix='?'+new URLSearchParams({location:locationCode});
    const endpoints=['cash-register','cashbox-history','cash-report'];
    const responses=await Promise.all(endpoints.map(endpoint=>
      fetch('/api/v1/staff/sales/'+endpoint+suffix,{
        credentials:'same-origin',cache:'no-store',
      })));
    const bodies=await Promise.all(responses.map(async r=>({
      status:r.status,value:await r.json() as {ok:boolean;data?:{
        register?:Register|null;counts?:Count[];sales?:CashSale[]}},
    })));
    if(bodies.some(r=>r.status>=400||!r.value.ok))
      throw Error('Unable to load Cashbox for this location.');
    setRegister(bodies[0].value.data?.register??null);
    setCounts(bodies[1].value.data?.counts??[]);
    setSales(bodies[2].value.data?.sales??[]);
  };
  useEffect(()=>{
    let active=true;
    setBusy(true);setError('');setResult('');setRegister(null);
    const suffix='?'+new URLSearchParams({location:locationCode});
    void Promise.all(['cash-register','cashbox-history','cash-report']
      .map(endpoint=>fetch('/api/v1/staff/sales/'+endpoint+suffix,{
        credentials:'same-origin',cache:'no-store',
      }).then(async response=>{
        const json=await response.json() as {ok:boolean;data?:{
          register?:Register|null;counts?:Count[];sales?:CashSale[]}};
        if(!response.ok||!json.ok)throw Error('Cashbox unavailable.');
        return json.data;
      }))).then(([a,b,c])=>{
        if(!active)return;
        setRegister(a?.register??null);setCounts(b?.counts??[]);setSales(c?.sales??[]);
      }).catch(e=>{if(active)setError(e instanceof Error?e.message:'Cashbox unavailable.');})
      .finally(()=>{if(active)setBusy(false);});
    return ()=>{active=false};
  },[locationCode]);
  const submit=async()=>{
    if(!register||saving)return;
    setSaving(true);setError('');setResult('');
    try{
      const response=await fetch('/api/v1/staff/sales/cash-register/count',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:locationCode,denominations:billsCount,note}),
      });
      const json=await response.json() as {ok:boolean;data?:{
        countedCents:number;expectedCents:number;varianceCents:number},
        error?:{code?:string}};
      if(!response.ok||!json.ok||!json.data)
        throw Error(json.error?.code??'Could not record Cashbox count.');
      const variance=json.data.varianceCents;
      setResult('Count saved. '+(variance===0?'Cashbox balances.':
        'Cashbox is '+money(Math.abs(variance))+(variance>0?' over.':' short.')));
      setBillsCount({});setNote('');
      await reload();
    }catch(e){setError(e instanceof Error?e.message:'Cashbox count unavailable.');}
    finally{setSaving(false);}
  };
  const last=counts[0];
  const variance=last?.varianceCents??null;
  return <section className="ecfs-cash-tender" aria-label="Cashbox">
    <h1>Cashbox</h1>
    <p>East Coast Float Spa {locationCode}</p>
    {busy&&<p role="status">Loading Cashbox…</p>}
    {error&&<p role="alert">{error}</p>}
    {!busy&&!register&&<p role="status">No Cashbox is configured for this location.</p>}
    {register&&<>
      <section aria-label="Cashbox summary">
        <h2>Cashbox Summary</h2>
        <p>Opening balance: <strong>{money(register.openingCents)}</strong>
          {register.synthetic?' (Staging test baseline)':''}</p>
        <p>Net cash sales: <strong>{money(register.netSalesCents)}</strong>
          {' '}({register.saleCount} transactions)</p>
        <p>Expected Cashbox total: <strong>{money(register.expectedCents)}</strong></p>
        {last
          ?<p role={variance!==0?'alert':'status'}>
            Last verified count: {money(last.countedCents)} against
            {' '}{money(last.expectedCents)} expected.
            {' '}{variance===0?'Balanced':variance!>0?
              money(variance!)+' over':money(Math.abs(variance!))+' short'}
            {' '}({new Date(last.countedAt).toLocaleString()})
          </p>
          :<p>No physical cash count recorded yet.</p>}
      </section>
      <section aria-label="Physical Cashbox count">
        <h2>Count Cashbox</h2>
        <p>Enter the bills and coins physically in the drawer. This is separate from
          entering the cash received in a sale.</p>
        <div className="ecfs-cash-denominations">
          {bills.map(unit=><label key={unit}>{name(unit)}
            <input aria-label={'Count '+name(unit)} type="number" min="0" step="1"
              value={billsCount[String(unit)]??0}
              onChange={e=>setBillsCount(current=>({...current,
                [unit]:Math.max(0,Math.min(100000,Number.parseInt(e.target.value,10)||0)),
              }))}/>
          </label>)}
        </div>
        <p>Physical count: <strong>{money(countValue(billsCount))}</strong>.
          {' '}Difference from expected: <strong>
            {money(countValue(billsCount)-register.expectedCents)}</strong></p>
        <label>Note (optional)
          <input value={note} maxLength={280} onChange={e=>setNote(e.target.value)}/>
        </label>
        <button type="button" disabled={saving} onClick={()=>void submit()}>
          {saving?'Saving…':'Save Cashbox Count'}
        </button>
        {result&&<p role="status">{result}</p>}
      </section>
      <section aria-label="Cashbox history">
        <h2>Count History</h2>
        {counts.length?counts.map(row=><p key={row.id}>
          {new Date(row.countedAt).toLocaleString()}:
          {' '}{money(row.countedCents)} counted,
          {' '}{money(row.expectedCents)} expected,
          {' '}{row.varianceCents===0?'balanced':
            money(Math.abs(row.varianceCents))+
              (row.varianceCents>0?' over':' short')}
          {row.note?' · '+row.note:''}
        </p>):<p>No prior counts.</p>}
      </section>
      <section aria-label="Cashbox sales history">
        <h2>Cash Sales</h2>
        {sales.length?sales.map(row=><p key={row.transactionId}>
          {new Date(row.createdAt).toLocaleString()} · {row.customerName}:
          {' '}{money(row.totalCents)} sale, {money(row.receivedCents)} tendered,
          {' '}{money(row.changeCents)} change
        </p>):<p>No cash sales yet.</p>}
      </section>
    </>}
  </section>;
}
